import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'i18next'
import { nanoid } from 'nanoid'
import { customAmplitude } from '@/utils/customAmplitude'
import api from '@/services/api'
import store, { type AppDispatch } from '@store/store'
import type { AIFashionModelItem } from '@/store/slices/aiFashionModels/types'
import type { ImageSetData } from '@/store/slices/types'
import { getAIFashionModels } from '@/store/slices/aiFashionModels/actions'
import { selectAIFashionModels } from '@/store/slices/aiFashionModels/selectors'
import { selectAIFashionResults, selectAIFashionSelectedResultId, selectAIFashionSession } from '@/store/slices/aiFashion/selectors'
import type { AIFashionSessionParams } from '@/store/slices/aiFashion/types'
import { setOpenModalOutOfCredits, setOpenModalTryPremium } from '@/store/slices/user/actions'
import { selectUser } from '@/store/slices/user/selectors'
import { setShowModalType } from '@/store/slices/modal/action'
import { ShowModalType } from '@/scenes/engine/common/constants'
import { resolveAIModelsAccessDecision } from '@/services/ai/aiAccessPolicy'
import { selectAIStudioAccessAllowed } from './aiStudioAccess'
import { getModelThumbnailUrl, getModelHighresUrl, getBackgroundThumbnailUrl } from '@/utils/getResizeUrl'
import { readImageFileAsCanvasReadyDataUrl, processImageForCanvas } from '@/utils/imageProcessingUtils'
import { IMAGE_UPLOAD_ACCEPT } from '@/utils/supportedImageFormats'
import {
  ASPECT_RATIO_MAP,
  getRatioBaseItems,
} from '@/scenes/Editor/components/FloatingInspectorButton/SharedComponents/Popover/ratioOptions'
import type {
  SuggestionButton,
} from '@/scenes/Editor/components/EditWithAI/components/suggestedButtonSets'
import BackgroundSuggestIcon from '@/components/icons/BackgroundSuggestIcon'
import ModelSuggestIcon from '@/components/icons/ModelSuggestIcon'
import PoseSuggestIcon from '@/components/icons/PoseSuggestIcon'
import ResizeSuggestIcon from '@/components/icons/ResizeSuggestIcon'
import type { AIFashionController } from '@/scenes/AIStudioLite/AIFashionController'
import type { PoseTile } from '@/scenes/engine/utils/aiFashionSession'
import {
  POSE_GRID_DIMENSION,
  POSE_GRID_DIMENSION_COLUMN,
} from '@/services/ai/AIFashionService'
import {
  BASIC_BACKGROUND_CATEGORY_ID,
  STUDIO_BACKGROUND_PRESETS,
  createAspectRatioListPopover,
  createBackgroundGridPopover,
  createModelGridPopover,
  createPoseGridPopover,
  createProductGridPopover,
} from '@/components/AIPopovers'
import { AI_MODELS_SOURCE, emitAIModelsUsageLimitReached } from '@/scenes/engine/utils/aiModelsAnalytics'

type UseAIFashionButtonSetDataOptions = {
  controller: AIFashionController | null
  targetId: string | undefined
  isReady: boolean
}

export type AIFashionButtonSetData = {
  handler: AIFashionController | null
  session: AIFashionSessionParams
  modelsPopoverData: { aspectRatio: number; data: AIFashionModelItem[] } | undefined
  backgroundPopoverData: ImageSetData | undefined
  poseTiles: PoseTile[]
  poseTilesLoading: boolean
  isLoadingMorePoses: boolean
  invalidatePoseTilesIfNeeded: () => void
  triggerPoseTilesLoad: () => void
  triggerLoadMorePoseTiles: () => void
  productImage: string | undefined
  uploadedProducts: { id: string; previewSrc: string }[]
  triggerFilePicker: (onComplete?: () => void) => void
  selectProductFromMenu: (id: string) => void
  syncParamsFromActiveObject: () => Promise<void>
  isAccessAllowed: boolean
  handlePoseClick: () => void
  handleChangeProductClick: () => void
}

/** Matches `getCurrentProductBase64(512)` for grid thumbnails. */
const PRODUCT_UPLOAD_GRID_PREVIEW_MAX_EDGE = 512

/** CSS aspect-ratio value for each session aspect-ratio option, used in the Pose tile grid. */
const ASPECT_RATIO_CSS_MAP: Record<string, string> = {
  square: '1/1',
  portrait: '3/4',
  portrait_4_5: '4/5',
  portrait_2_3: '2/3',
  portrait_9_16: '9/16',
}

/** Maps a catalogue model item to the `AIFashionTemplate` shape `setTemplate`/`generate` expect. */
export function modelItemToTemplate(item: AIFashionModelItem, index: number) {
  return {
    id: item.id ?? String(index),
    title: item.title,
    imageUrl: item.src ? getModelHighresUrl(item.src) : item.src,
    prompt: item.prompt,
    gender: item.gender,
    age: item.age,
    categoryId: item.classificationId,
  }
}

const withOnClick = <T,>(
  items: T[],
  getValue: (item: T) => string,
  onActionSelect: (action: string) => void,
): (T & { onClick: () => void })[] =>
  items.map((item) => ({ ...item, onClick: () => onActionSelect(getValue(item)) }))

export function useAIFashionButtonSetData({
  controller,
  targetId,
  isReady,
}: UseAIFashionButtonSetDataOptions): AIFashionButtonSetData {
  const dispatch = useDispatch<AppDispatch>()
  const modelsPopoverData = useSelector(selectAIFashionModels)
  const session = useSelector(selectAIFashionSession)
  const results = useSelector(selectAIFashionResults)
  const selectedResultId = useSelector(selectAIFashionSelectedResultId)
  const isAccessAllowed = useSelector(selectAIStudioAccessAllowed)
  const modelsPopoverDataRef = useRef(modelsPopoverData)
  const [backgroundPopoverData, setBackgroundPopoverData] = useState<ImageSetData | undefined>(undefined)
  // Set only when the user uploads/picks a new product (which is intentionally
  // not added to the results), so it overrides the product-record image.
  const [userProductImage, setUserProductImage] = useState<string | undefined>(undefined)
  const [uploadedProducts, setUploadedProducts] = useState<{ id: string; previewSrc: string }[]>([])
  const productFullSrcById = useRef(new Map<string, string>())
  const [poseTiles, setPoseTiles] = useState<PoseTile[]>([])
  const [poseTilesLoading, setPoseTilesLoading] = useState(false)
  const [isLoadingMorePoses, setIsLoadingMorePoses] = useState(false)

  useEffect(() => {
    if (!modelsPopoverDataRef.current) {
      dispatch(getAIFashionModels())
    }
    api.getAIBackgroundModels().then((items) => {
      if (items.data.length > 0) setBackgroundPopoverData(items)
    }).catch(() => {})
  }, [dispatch])

  const invalidatePoseTilesIfNeeded = useCallback(() => {
    if (controller) {
      setPoseTiles(controller.invalidatePoseTiles())
      setPoseTilesLoading(false)
      setIsLoadingMorePoses(false)
    }
  }, [controller])

  const replaceProductAndGenerate = useCallback(async (fullSrc: string, productId: string, onComplete?: () => void) => {
    if (!controller || !isReady) return
    controller.setProductImage(fullSrc, productId)
    setUserProductImage(fullSrc)
    setPoseTiles(controller.invalidatePoseTiles())
    setPoseTilesLoading(false)
    setIsLoadingMorePoses(false)
    onComplete?.()
    if (!isAccessAllowed) return
    await controller.triggerProcess()
  }, [controller, isAccessAllowed, isReady])

  const selectProductFromMenu = useCallback((id: string) => {
    const fullSrc = productFullSrcById.current.get(id)
    if (!fullSrc) return
    void replaceProductAndGenerate(fullSrc, id)
  }, [replaceProductAndGenerate])

  const triggerPoseTilesLoad = useCallback(() => {
    controller?.fetchPoseTiles((tiles, loading) => {
      setPoseTiles(tiles)
      setPoseTilesLoading(loading)
    })
  }, [controller])

  const triggerLoadMorePoseTiles = useCallback(() => {
    controller?.fetchMorePoseTiles((tiles, more) => {
      setPoseTiles(tiles)
      setIsLoadingMorePoses(more)
    })
  }, [controller])

  const triggerFilePicker = useCallback((onComplete?: () => void) => {
    if (!controller || !isReady) return
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = IMAGE_UPLOAD_ACCEPT

    input.addEventListener('change', async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const dataUrl = await readImageFileAsCanvasReadyDataUrl(file)
        const thumbSrc = await processImageForCanvas(dataUrl, {
          maxEdgeSize: PRODUCT_UPLOAD_GRID_PREVIEW_MAX_EDGE,
          extractBoundingBox: false,
        })
        const newProductImageKey = nanoid()
        productFullSrcById.current.set(newProductImageKey, dataUrl)
        setUploadedProducts(prev => [...prev, { id: newProductImageKey, previewSrc: thumbSrc }])
        await replaceProductAndGenerate(dataUrl, newProductImageKey, onComplete)
      } catch (err) {
        console.error('Failed to process product image:', err)
      }
    }, { once: true })

    input.click()
  }, [controller, isReady, replaceProductAndGenerate])

  useEffect(() => {
    if (!controller || !isReady) return
    let cancelled = false
    const wasRestored = controller.loadParamsFromGeneratedObject(targetId)
    if (!wasRestored) {
      controller.getCurrentProductReference(512).then((productReference) => {
        if (!cancelled && productReference) {
          const { url, productImageCacheKey: initialId } = productReference
          setUploadedProducts(prev => {
            if (prev.length > 0) return prev
            productFullSrcById.current.set(initialId, url)
            return [{ id: initialId, previewSrc: url }]
          })
        }
      })
      return () => { cancelled = true }
    }

    controller.restoreGeneratedObjectToSession(targetId).then((restoredProduct) => {
      if (cancelled) return
      if (restoredProduct) {
        const { url: restoredProductUrl, productImageCacheKey: restoredId } = restoredProduct
        setUploadedProducts(prev => {
          if (prev.length > 0) return prev
          productFullSrcById.current.set(restoredId, restoredProductUrl)
          return [{ id: restoredId, previewSrc: restoredProductUrl }]
        })
      }
      setPoseTiles(controller.getCachedPoseTiles())
      setPoseTilesLoading(false)
      setIsLoadingMorePoses(false)
    })
    return () => { cancelled = true }
  }, [targetId, controller, isReady])

  const syncParamsFromActiveObject = useCallback(async () => {
    if (!controller || !isReady) return
    await controller.restoreGeneratedObjectToSession(targetId)
  }, [controller, isReady, targetId])

  const allowAIModelsAction = useCallback(() => {
    const state = store.getState()
    if (!selectUser(state)) {
      dispatch(setShowModalType(ShowModalType.SIGN_IN))
      return false
    }
    const access = resolveAIModelsAccessDecision(state)
    if (access.allowed) return true
    if (access.reason === 'premiumOutOfCredits') {
      dispatch(setOpenModalOutOfCredits({ isOpen: true }))
      emitAIModelsUsageLimitReached()
    } else {
      dispatch(setOpenModalTryPremium({
        isOpen: true,
        source: AI_MODELS_SOURCE,
        placeholderImage: selectAIFashionSession(state).selectedTemplate?.imageUrl,
      }))
      customAmplitude('Premium Prompt', { Source: AI_MODELS_SOURCE, Type: 'Standard' })
    }
    return false
  }, [dispatch])

  const handlePoseClick = useCallback(() => {
    if (allowAIModelsAction()) triggerPoseTilesLoad()
  }, [allowAIModelsAction, triggerPoseTilesLoad])

  const selectedResultProduct = React.useMemo(() => {
    const selected = results.find(r => r.objectId === selectedResultId)
    const result = selected?.kind === 'video'
      ? results.find(r => r.objectId === selected.sourceResultId)
      : selected
    if (!result) return null
    if (result.kind === 'generated') return result.generationParams.product.dataUrl ?? null
    if (result.kind === 'product') return result.result.dataUrl ?? null
    return null
  }, [results, selectedResultId])

  // Precedence:
  //  1. selectedResultProduct — product used to generate the currently selected result.
  //  2. userProductImage      — product the user just picked (no result yet / loading state).
  //  3. session fallback      — Editor context (no product record) / first render before sync.
  const productImage = selectedResultProduct
    ?? userProductImage
    ?? session.productRepoEntry?.dataUrl
    ?? controller?.getSession().cachedProductBase64
    ?? undefined

  return {
    handler: controller,
    session,
    modelsPopoverData,
    backgroundPopoverData,
    poseTiles,
    poseTilesLoading,
    isLoadingMorePoses,
    invalidatePoseTilesIfNeeded,
    triggerPoseTilesLoad,
    triggerLoadMorePoseTiles,
    productImage,
    uploadedProducts,
    triggerFilePicker,
    selectProductFromMenu,
    syncParamsFromActiveObject,
    isAccessAllowed,
    handlePoseClick,
    handleChangeProductClick: allowAIModelsAction,
  }
}

export function getAIFashionButtons(
  {
    handler,
    session,
    modelsPopoverData,
    backgroundPopoverData,
    poseTiles,
    isLoadingMorePoses,
    invalidatePoseTilesIfNeeded,
    triggerLoadMorePoseTiles,
    productImage,
    uploadedProducts,
    triggerFilePicker,
    selectProductFromMenu,
    syncParamsFromActiveObject,
    isAccessAllowed,
    handlePoseClick,
    handleChangeProductClick,
  }: AIFashionButtonSetData,
  triggerGeneration?: (loadingLabel?: string) => void,
): SuggestionButton[] {
  const generateAfterStateUpdate = (loadingLabel?: string) => {
    if (triggerGeneration) {
      triggerGeneration(loadingLabel)
    }
  }

  const backgroundSelect = async (value: string) => {
    await syncParamsFromActiveObject()
    handler?.setBackground(value)
    invalidatePoseTilesIfNeeded()
    generateAfterStateUpdate(t('Changing background'))
  }

  const resizeSelect = async (value: string) => {
    await syncParamsFromActiveObject()
    handler?.setAspectRatio(ASPECT_RATIO_MAP[value] ?? null)
    invalidatePoseTilesIfNeeded()
    generateAfterStateUpdate(t('Changing size to {{arg}}', { arg: value }))
  }

  const modelSelect = async (value: string) => {
    await syncParamsFromActiveObject()
    if (modelsPopoverData?.data) {
      const idx = modelsPopoverData.data.findIndex((m) => m.title === value)
      if (idx >= 0) {
        handler?.setTemplate(modelItemToTemplate(modelsPopoverData.data[idx], idx))
      }
    }
    customAmplitude('Click', { Source: 'choose.ai_models' })
    invalidatePoseTilesIfNeeded()
    generateAfterStateUpdate(t('Changing model'))
  }

  const poseGridItems = poseTiles.map(tile => ({
    src: tile.src,
    title: tile.title,
    onClick: async () => {
      await syncParamsFromActiveObject()
      handler?.setCompositionOverride(tile.composition, tile.src)
      generateAfterStateUpdate(t('Changing pose'))
    },
  }))

  const poseAspectRatioCss = (session.aspectRatioOption
    && ASPECT_RATIO_CSS_MAP[session.aspectRatioOption])
    ?? '2/3'

  const sizeAspectRatioLabel = session.aspectRatioOption
    ? Object.keys(ASPECT_RATIO_MAP).find(k => ASPECT_RATIO_MAP[k] === session.aspectRatioOption)
    : null

  const sizeLabel = sizeAspectRatioLabel ? t(`Size ({{arg}})`, { arg: sizeAspectRatioLabel }) : t('Size')

  const poseButton: SuggestionButton = {
    title: t('Pose'),
    icon: React.createElement(PoseSuggestIcon, { disabled: false }),
    onClick: handlePoseClick,
    popover: isAccessAllowed ? createPoseGridPopover({
      title: t('Pose'),
      columns: POSE_GRID_DIMENSION_COLUMN,
      skeletonCount: POSE_GRID_DIMENSION * POSE_GRID_DIMENSION,
      aspectRatio: poseAspectRatioCss,
      items: poseGridItems,
      onLoadMore: triggerLoadMorePoseTiles,
      isLoadingMore: isLoadingMorePoses,
      skeletonVariant: 'gradient',
    }) : undefined,
  }

  const changeProductButton: SuggestionButton = {
    title: t('Change product'),
    imageSrc: productImage,
    buttonStyle: 'imageInCircle',
    onClick: handleChangeProductClick,
    popover: isAccessAllowed ? createProductGridPopover({
      title: t('Change product'),
      items: uploadedProducts.map(p => ({
        src: p.previewSrc,
        title: p.id,
        onClick: () => selectProductFromMenu(p.id),
      })),
      onLoadMore: triggerFilePicker,
    }) : undefined,
  }

  return [
    poseButton,
    {
      title: t('Background'),
      icon: React.createElement(BackgroundSuggestIcon, { disabled: false }),
      popover: createBackgroundGridPopover({
        title: t('Background'),
        aspectRatio: backgroundPopoverData?.aspectRatio ?? '1/1',
        items: [
          ...STUDIO_BACKGROUND_PRESETS.map(preset => ({
            src: '',
            title: preset.name,
            prompt: preset.prompt,
            topHex: preset.topHex,
            bottomHex: preset.bottomHex,
            categoryId: BASIC_BACKGROUND_CATEGORY_ID,
            onClick: () => backgroundSelect(preset.prompt),
          })),
          ...(backgroundPopoverData?.classifications ?? []).flatMap(c =>
            c.items.map(item => ({
              src: item.src ? getBackgroundThumbnailUrl(item.src) : item.src,
              title: item.title,
              prompt: item.prompt,
              categoryId: String(c.id),
              onClick: () => backgroundSelect(item.prompt ?? item.title),
            }))
          ),
        ],
        categories: [
          { id: BASIC_BACKGROUND_CATEGORY_ID, label: t('Basic') },
          ...(backgroundPopoverData?.classifications ?? []).map(c => ({
            id: String(c.id),
            label: c.title,
          })),
        ],
      }),
    },
    {
      title: t('Model'),
      icon: React.createElement(ModelSuggestIcon, { disabled: false }),
      popover: createModelGridPopover({
        title: t('Model'),
        aspectRatio: modelsPopoverData?.aspectRatio ?? '2/3',
        items: withOnClick(
          (modelsPopoverData?.data ?? []).map(item => ({
            ...item,
            src: item.src ? getModelThumbnailUrl(item.src) : item.src,
          })),
          (i) => i.title,
          modelSelect
        ),
      }),
    },
    {
      title: sizeLabel,
      icon: React.createElement(ResizeSuggestIcon, { disabled: false }),
      popover: createAspectRatioListPopover({
        title: t('Size'),
        items: withOnClick(getRatioBaseItems(), (i) => i.value, resizeSelect),
      }),
    },
    changeProductButton,
  ]
}
